import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import MainLayout from '../components/layout/MainLayout';
import { HelpButton } from '../components/ui/help-button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Cpu, Radar, Server, Shield, Plus, Play, Eye, Trash2, AlertTriangle,
  CheckCircle, Clock, Loader2, Globe, Network, Link as LinkIcon, RefreshCw, Info, Wrench, ArrowRight
} from 'lucide-react';
import { getUserErrorMessage } from '../utils/errorMessages';
import { toast } from 'sonner';
import WizardStepper from '../components/WizardStepper';
import WizardNavButtons from '../components/WizardNavButtons';
import { Coachmark } from '../components/Coachmark';
import { SteekproefBanner } from '../components/SteekproefBanner';

const TechnologyPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [targets, setTargets] = useState([]);
  const [scans, setScans] = useState([]);
  const [showAddTargetModal, setShowAddTargetModal] = useState(false);
  const [showScanModal, setShowScanModal] = useState(false);
  const [showScanDetailsModal, setShowScanDetailsModal] = useState(false);
  const [selectedScan, setSelectedScan] = useState(null);
  const [creatingMeasure, setCreatingMeasure] = useState(null);
  const [creating, setCreating] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [usageLimits, setUsageLimits] = useState({ scans: { used: 0, limit: 1 } });
  const [moduleStats, setModuleStats] = useState(null);
  const [steekproefCtx, setSteekproefCtx] = useState(null);

  const [targetType, setTargetType] = useState('domain');
  const [targetValue, setTargetValue] = useState('');
  const [targetName, setTargetName] = useState('');
  const [targetDescription, setTargetDescription] = useState('');
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [scanType, setScanType] = useState('quick');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [statsRes, targetsRes, scansRes, limitsRes, peopleS, processS, techS, spCtx] = await Promise.all([
        api.get('/technology/stats'),
        api.get('/technology/targets'),
        api.get('/technology/scans'),
        api.get('/ggrc/usage-limits').catch(() => ({ data: { scans: { used: 0, limit: 1 } } })),
        api.get('/people/stats').catch(() => ({ data: { completed: 0, total: 10, score: 0 } })),
        api.get('/process/stats').catch(() => ({ data: { completed: 0, total: 10, score: 0 } })),
        api.get('/technology/stats').catch(() => ({ data: { completed: 0, total: 10, score: 0 } })),
        api.get('/assessments/active-reassessment-context').catch(() => ({ data: { active: false } })),
      ]);
      setStats(statsRes.data);
      setTargets(targetsRes.data);
      setScans(scansRes.data);
      setUsageLimits(limitsRes.data || { scans: { used: 0, limit: 1 } });
      setModuleStats({
        people: peopleS.data || { completed: 0, total: 10, score: 0 },
        process: processS.data || { completed: 0, total: 10, score: 0 },
        technology: techS.data || { completed: 0, total: 10, score: 0 },
      });
      setSteekproefCtx(spCtx.data);
    } catch (error) {
      toast.error('Data laden mislukt');
    } finally { setLoading(false); }
  };

  const canStartScan = usageLimits.scans?.used < usageLimits.scans?.limit;

  const handleAddTarget = async () => {
    if (!targetValue.trim()) { toast.error('Voer een target waarde in'); return; }
    setCreating(true);
    try {
      await api.post('/technology/targets', { target_type: targetType, target_value: targetValue, name: targetName || targetValue, description: targetDescription || null });
      toast.success('Target toegevoegd');
      setShowAddTargetModal(false);
      resetTargetForm();
      fetchData();
    } catch (error) { toast.error(getUserErrorMessage(error, 'Het doelwit kon niet worden toegevoegd. Controleer de invoer.')); } finally { setCreating(false); }
  };

  const handleDeleteTarget = async (targetId) => {
    if (!window.confirm('Weet u zeker dat u dit target wilt verwijderen?')) return;
    try { await api.delete(`/technology/targets/${targetId}`); toast.success('Target verwijderd'); fetchData(); } catch (error) { toast.error(getUserErrorMessage(error, 'Het doelwit kon niet worden verwijderd. Probeer het later opnieuw.')); }
  };

  const handleStartScan = async () => {
    if (selectedTargets.length === 0) { toast.error('Selecteer minimaal een target'); return; }
    setScanning(true);
    try {
      await api.post('/technology/scans', { target_ids: selectedTargets, scan_type: scanType });
      toast.success('Scan gestart');
      setShowScanModal(false);
      setSelectedTargets([]);
      fetchData();
      setTimeout(() => fetchData(), 6000);
    } catch (error) {
      const detail = getUserErrorMessage(error, 'De scan kon niet worden gestart. Probeer het later opnieuw.');
      toast.error(detail);
    } finally { setScanning(false); }
  };

  const handleViewScan = async (scanId) => {
    try { const res = await api.get(`/technology/scans/${scanId}`); setSelectedScan(res.data); setShowScanDetailsModal(true); }
    catch { toast.error('Details laden mislukt'); }
  };

  const resetTargetForm = () => { setTargetType('domain'); setTargetValue(''); setTargetName(''); setTargetDescription(''); };
  const toggleTargetSelection = (id) => setSelectedTargets(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  const getTargetTypeIcon = (type) => ({ domain: Globe, ip: Network, url: LinkIcon }[type] || Server);

  const getStatusBadge = (status) => {
    const map = { pending: { c: 'border-neutral-200 bg-neutral-50 text-neutral-600', l: 'Wachtend' }, running: { c: 'border-blue-200 bg-blue-50 text-blue-700', l: 'Bezig' }, completed: { c: 'border-green-200 bg-green-50 text-green-700', l: 'Afgerond' }, failed: { c: 'border-red-200 bg-red-50 text-red-700', l: 'Mislukt' } };
    const m = map[status] || map.pending;
    return <Badge className={m.c}>{m.l}</Badge>;
  };
  const getSeverityBadge = (sev) => {
    const map = { critical: { c: 'border-red-300 bg-red-100 text-red-800', l: 'Kritiek' }, high: { c: 'border-orange-300 bg-orange-100 text-orange-800', l: 'Hoog' }, medium: { c: 'border-yellow-300 bg-yellow-100 text-yellow-800', l: 'Medium' }, low: { c: 'border-blue-200 bg-blue-50 text-blue-700', l: 'Laag' }, info: { c: 'border-neutral-200 bg-neutral-50 text-neutral-600', l: 'Info' } };
    const m = map[sev] || map.info;
    return <Badge className={m.c}>{m.l}</Badge>;
  };

  const getSeverityLabel = (sev) => ({ critical: 'Kritiek', high: 'Hoog', medium: 'Medium', low: 'Laag', info: 'Info' }[sev] || sev);

  const getVulnRecommendation = (name) => {
    const recommendations = {
      'SQL Injection Possible': 'Gebruik parameterized queries (prepared statements) in plaats van string concatenation. Valideer en sanitize alle gebruikersinvoer. Overweeg een Web Application Firewall (WAF) te implementeren.',
      'XSS Vulnerability': 'Implementeer Content Security Policy (CSP) headers. Escape alle gebruikersinvoer bij weergave. Gebruik frameworks die automatisch XSS-bescherming bieden.',
      'Open Port Detected': 'Sluit alle poorten die niet noodzakelijk zijn voor bedrijfsvoering. Configureer een firewall om alleen benodigde poorten toe te staan. Monitor open poorten regelmatig.',
      'Outdated SSL/TLS': 'Upgrade naar TLS 1.3 of minimaal TLS 1.2. Schakel verouderde protocollen (SSLv3, TLS 1.0, 1.1) uit. Vernieuw SSL-certificaten voor de vervaldatum.',
      'Missing Security Headers': 'Voeg X-Content-Type-Options, X-Frame-Options, Strict-Transport-Security en Content-Security-Policy headers toe aan uw webserver configuratie.',
      'Default Credentials': 'Wijzig alle standaard wachtwoorden onmiddellijk. Implementeer een sterk wachtwoordbeleid. Gebruik een password manager voor het beheren van credentials.',
    };
    return recommendations[name] || 'Raadpleeg uw security team voor specifieke remediatie-stappen. Prioriteer op basis van de ernst en de blootstelling van het systeem.';
  };

  const handleCreateMeasureFromVuln = async (vuln) => {
    setCreatingMeasure(vuln.id);
    try {
      await api.post('/ggrc/measures', {
        name: `Verhelp: ${vuln.name}`,
        description: `Kwetsbaarheid "${vuln.name}" gedetecteerd op ${vuln.target_value}. ${vuln.description || ''}`,
        goal: getVulnRecommendation(vuln.name),
        domain: 'technology',
        factor: vuln.severity === 'critical' || vuln.severity === 'high' ? 0.8 : 0.5,  // Float: reduction factor
        complexity: vuln.severity === 'critical' ? 'high' : 'medium',  // Literal: low/medium/high
        impact: vuln.severity === 'critical' ? 5 : vuln.severity === 'high' ? 4 : 3,  // Integer 1-5
        linked_vulnerability_ids: [vuln.id],
      });
      toast.success('Maatregel aangemaakt vanuit kwetsbaarheid');
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'De maatregel kon niet worden aangemaakt. Probeer het later opnieuw.'));
    } finally { setCreatingMeasure(null); }
  };

  if (loading) {
    return (<MainLayout><div className="flex items-center justify-center h-[calc(100vh-4rem)]"><Loader2 className="h-8 w-8 animate-spin text-neutral-400" strokeWidth={1.5} /></div></MainLayout>);
  }

  return (
    <MainLayout>
      <div className="min-h-screen bg-white">
        <div className="mx-auto max-w-5xl px-6 py-8 space-y-10">
        <WizardStepper moduleStats={moduleStats} />
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-neutral-400 mb-1">Technology Module</p>
            <h1 className="text-2xl font-bold tracking-tight text-black">Vulnerability Scans</h1>
            <p className="text-sm text-neutral-600 mt-1">Scan uw infrastructuur op kwetsbaarheden en beveiligingsrisico's</p>
          </div>
          <div className="flex items-center gap-3">
            <HelpButton steps={[
              { title: 'Scan Targets', description: 'Voeg domeinen, IP-adressen of URL\'s toe die u wilt scannen op kwetsbaarheden.', target: '[data-testid="add-target-button"]' },
              { title: 'Vulnerability Scan', description: 'Start een snelle of volledige scan om kwetsbaarheden automatisch te detecteren.', target: '[data-testid="start-scan-button"]', tip: 'Een volledige scan duurt langer maar vindt meer kwetsbaarheden dan een snelle scan.' },
              { title: 'Resultaten', description: 'Bekijk gevonden kwetsbaarheden per scan en maak direct een maatregel aan om het probleem te verhelpen.', target: null },
            ]} />
            <Button variant="outline" className="border-neutral-200" onClick={() => setShowAddTargetModal(true)} data-testid="add-target-button">
              <Plus className="mr-2 h-4 w-4" strokeWidth={1.5} />Target Toevoegen
            </Button>
            <Button className={`${targets.length === 0 ? 'bg-neutral-300 text-neutral-500 cursor-not-allowed' : 'bg-black text-white hover:bg-neutral-800'}`}
              onClick={() => setShowScanModal(true)}
              disabled={targets.length === 0}
              data-testid="start-scan-button">
              <Radar className="mr-2 h-4 w-4" strokeWidth={1.5} />Start Scan
            </Button>
          </div>
        </div>

        {/* Coachmark */}
        <Coachmark
          id="technology-intro"
          text="Voeg eerst je domeinen en IP-adressen toe als 'targets'. Start daarna een scan om kwetsbaarheden te detecteren."
        />

        {/* Steekproef Banner */}
        {steekproefCtx?.active && (
          <SteekproefBanner
            active={true}
            moduleName="technology"
            moduleLabel="Systemen"
            reassessment={steekproefCtx.reassessment}
            nulmetingSnapshot={steekproefCtx.nulmeting_snapshot}
            isModuleIncluded={(steekproefCtx.reassessment?.modules || []).includes('technology') || (steekproefCtx.reassessment?.modules || []).includes('all')}
          />
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: Server, val: stats?.total_targets || 0, lbl: 'Targets', bg: 'blue' },
            { icon: Radar, val: stats?.completed_scans || 0, lbl: 'Scans voltooid', bg: 'green' },
            { icon: AlertTriangle, val: stats?.total_vulnerabilities || 0, lbl: 'Kwetsbaarheden', bg: 'red' },
            { icon: Shield, val: stats?.security_score ?? '-', lbl: 'Security Score', bg: 'purple' },
          ].map((s, i) => (
            <Card key={i} className="border-neutral-200 shadow-sm">
              <CardContent className="p-5"><div className="flex items-center gap-3">
                <div className={`h-10 w-10 rounded-lg bg-${s.bg}-50${s.bg}-900/30 flex items-center justify-center`}>
                  <s.icon className={`h-5 w-5 text-${s.bg}-600`} strokeWidth={1.5} />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${s.lbl === 'Security Score' ? (stats?.security_score >= 70 ? 'text-green-600' : stats?.security_score >= 40 ? 'text-orange-500' : 'text-red-500') : 'text-black'}`}>{s.val}</p>
                  <p className="text-xs text-neutral-500">{s.lbl}</p>
                </div>
              </div></CardContent>
            </Card>
          ))}
        </div>

        {/* Latest Vulnerability Summary */}
        {stats?.latest_summary && (
          <Card className="border-neutral-200 shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-xs font-bold uppercase tracking-wider text-neutral-400">Laatste Scan Resultaten</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-5 gap-6">
                {[
                  { key: 'critical', label: 'Kritiek', color: 'bg-red-500' },
                  { key: 'high', label: 'Hoog', color: 'bg-orange-500' },
                  { key: 'medium', label: 'Medium', color: 'bg-yellow-500' },
                  { key: 'low', label: 'Laag', color: 'bg-blue-400' },
                  { key: 'info', label: 'Info', color: 'bg-neutral-400' },
                ].map(({ key, label, color }) => (
                  <div key={key} className="text-center">
                    <div className={`${color} text-white text-2xl font-bold py-4 rounded-lg`}>{stats.latest_summary[key] || 0}</div>
                    <p className="text-xs text-neutral-500 mt-2">{label}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Targets List */}
        <Card className="border-neutral-200 shadow-sm">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-xs font-bold uppercase tracking-wider text-neutral-400">Scan Targets</CardTitle>
            <Button variant="ghost" size="sm" onClick={fetchData} className="text-neutral-500 hover:text-black"><RefreshCw className="h-4 w-4" strokeWidth={1.5} /></Button>
          </CardHeader>
          <CardContent>
            {targets.length > 0 ? (
              <div className="divide-y divide-neutral-100">
                {targets.map(target => {
                  const Icon = getTargetTypeIcon(target.target_type);
                  return (
                    <div key={target.id} className="flex items-center gap-4 py-4" data-testid={`target-row-${target.id}`}>
                      <div className="h-10 w-10 rounded-lg bg-neutral-100 flex items-center justify-center">
                        <Icon className="h-5 w-5 text-neutral-600" strokeWidth={1.5} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-black">{target.name}</p>
                        <p className="text-xs text-neutral-500">{target.target_type.toUpperCase()}: {target.target_value}</p>
                      </div>
                      <div className="text-right">
                        {target.vulnerability_count > 0 ? <Badge className="border-red-200 bg-red-50 text-red-700">{target.vulnerability_count} kwetsbaarheden</Badge>
                          : target.last_scan_at ? <Badge className="border-green-200 bg-green-50 text-green-700">Geen issues</Badge>
                          : <Badge className="border-neutral-200 bg-neutral-50 text-neutral-600">Niet gescand</Badge>}
                      </div>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeleteTarget(target.id)}>
                        <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                      </Button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="py-12 text-center">
                <Server className="h-12 w-12 text-neutral-300 mx-auto mb-4" strokeWidth={1.5} />
                <h3 className="text-lg font-medium text-black mb-2">Geen targets</h3>
                <p className="text-sm text-neutral-500 mb-4">Voeg scan targets toe om te beginnen met vulnerability scanning.</p>
                <Button variant="outline" className="border-neutral-200" onClick={() => setShowAddTargetModal(true)}>
                  <Plus className="mr-2 h-4 w-4" strokeWidth={1.5} />Eerste Target
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Scans */}
        <Card className="border-neutral-200 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-xs font-bold uppercase tracking-wider text-neutral-400">Recente Scans</CardTitle></CardHeader>
          <CardContent>
            {scans.length > 0 ? (
              <div className="divide-y divide-neutral-100">
                {scans.slice(0, 10).map(scan => (
                  <div key={scan.id} className="flex items-center gap-4 py-4" data-testid={`scan-row-${scan.id}`}>
                    <div className="h-10 w-10 rounded-lg bg-neutral-100 flex items-center justify-center">
                      {scan.status === 'running' ? <Loader2 className="h-5 w-5 text-blue-600 animate-spin" strokeWidth={1.5} />
                        : scan.status === 'completed' ? <CheckCircle className="h-5 w-5 text-green-600" strokeWidth={1.5} />
                        : <Clock className="h-5 w-5 text-neutral-400" strokeWidth={1.5} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-black">{scan.scan_type === 'quick' ? 'Snelle Scan' : 'Volledige Scan'}</p>
                      <p className="text-xs text-neutral-500">{scan.target_count} target(s) - {new Date(scan.created_at).toLocaleString('nl-NL')}</p>
                    </div>
                    <div className="text-right">
                      {getStatusBadge(scan.status)}
                      {scan.status === 'completed' && <p className="text-xs text-neutral-500 mt-1">{scan.vulnerabilities?.length || 0} kwetsbaarheden</p>}
                    </div>
                    <Button variant="outline" size="sm" className="border-neutral-200" onClick={() => handleViewScan(scan.id)}>
                      <Eye className="h-3 w-3 mr-1" strokeWidth={1.5} />Details
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center"><p className="text-sm text-neutral-500">Nog geen scans uitgevoerd</p></div>
            )}
          </CardContent>
        </Card>

        {/* Volgende stap */}
        <section className="rounded-2xl border border-neutral-200 p-6" data-testid="technology-next-step">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <p className="spk-section-label mb-0">Volgende stap</p>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-black text-white">Aanbevolen</span>
              </div>
              <h3 className="text-lg font-semibold mt-1">Bekijk je nulmeting-resultaten</h3>
              <p className="text-sm text-neutral-600 mt-1">Alle drie domeinen ingevuld? Bekijk het totaalbeeld en ontvang je risico-analyse.</p>
            </div>
            <Button
              onClick={() => window.location.href = '/assessment'}
              className="bg-black text-white hover:bg-neutral-800 shrink-0"
              data-testid="technology-goto-assessment"
            >
              Resultaten bekijken
              <ArrowRight className="h-4 w-4 ml-2" strokeWidth={1.5} />
            </Button>
          </div>
        </section>

        <WizardNavButtons />
      </div>
      </div>

      {/* Add Target Modal */}
      <Dialog open={showAddTargetModal} onOpenChange={setShowAddTargetModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Nieuw Scan Target</DialogTitle><DialogDescription>Voeg een domein, IP-adres of URL toe om te scannen.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-3"><Label>Type</Label><Select value={targetType} onValueChange={setTargetType}><SelectTrigger data-testid="target-type-select"><SelectValue placeholder="Selecteer type" /></SelectTrigger><SelectContent><SelectItem value="domain">Domein</SelectItem><SelectItem value="ip">IP-adres</SelectItem><SelectItem value="url">URL</SelectItem></SelectContent></Select></div>
            <div className="space-y-3"><Label>{targetType === 'domain' ? 'Domein' : targetType === 'ip' ? 'IP-adres' : 'URL'}</Label><Input value={targetValue} onChange={e => setTargetValue(e.target.value)} placeholder={targetType === 'domain' ? 'example.com' : targetType === 'ip' ? '192.168.1.1' : 'https://example.com'} data-testid="target-value-input" /></div>
            <div className="space-y-3"><Label>Naam (optioneel)</Label><Input value={targetName} onChange={e => setTargetName(e.target.value)} placeholder="Productie Server" data-testid="target-name-input" /></div>
            <div className="space-y-3"><Label>Beschrijving (optioneel)</Label><Input value={targetDescription} onChange={e => setTargetDescription(e.target.value)} placeholder="Hoofdwebsite" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddTargetModal(false); resetTargetForm(); }}>Annuleren</Button>
            <Button className="bg-black text-white hover:bg-neutral-800" onClick={handleAddTarget} disabled={creating} data-testid="add-target-submit">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Toevoegen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Start Scan Modal */}
      <Dialog open={showScanModal} onOpenChange={setShowScanModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Nieuwe Scan Starten</DialogTitle><DialogDescription>Selecteer de targets die u wilt scannen.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-3"><Label>Scan Type</Label><Select value={scanType} onValueChange={setScanType}><SelectTrigger data-testid="scan-type-select"><SelectValue placeholder="Selecteer type" /></SelectTrigger><SelectContent><SelectItem value="quick">Snelle Scan</SelectItem><SelectItem value="full">Volledige Scan</SelectItem></SelectContent></Select></div>
            <div className="space-y-3">
              <Label>Selecteer Targets</Label>
              <div className="border border-neutral-200 rounded-lg max-h-64 overflow-y-auto">
                {targets.map(target => {
                  const Icon = getTargetTypeIcon(target.target_type);
                  const isSelected = selectedTargets.includes(target.id);
                  return (
                    <button key={target.id} type="button" onClick={() => toggleTargetSelection(target.id)}
                      className={`w-full flex items-center gap-3 p-3 text-left border-b border-neutral-100 last:border-0 transition-colors ${isSelected ? 'bg-neutral-100' : 'hover:bg-neutral-50'}`}
                      data-testid={`scan-target-${target.id}`}>
                      <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${isSelected ? 'bg-black' : 'bg-neutral-100'}`}>
                        {isSelected ? <CheckCircle className="h-4 w-4 text-white" strokeWidth={1.5} /> : <Icon className="h-4 w-4 text-neutral-500" strokeWidth={1.5} />}
                      </div>
                      <div><p className="text-sm font-medium text-black">{target.name}</p><p className="text-xs text-neutral-500">{target.target_value}</p></div>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-neutral-500">{selectedTargets.length} target(s) geselecteerd</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowScanModal(false); setSelectedTargets([]); }}>Annuleren</Button>
            <Button className="bg-black text-white hover:bg-neutral-800" onClick={handleStartScan} disabled={scanning || selectedTargets.length === 0} data-testid="start-scan-submit">
              {scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Start Scan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scan Details Modal — Enhanced */}
      <Dialog open={showScanDetailsModal} onOpenChange={setShowScanDetailsModal}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Scan Resultaten</DialogTitle>
            <DialogDescription>{selectedScan?.scan_type === 'quick' ? 'Snelle Scan' : 'Volledige Scan'} - {selectedScan && new Date(selectedScan.created_at).toLocaleString('nl-NL')}</DialogDescription>
          </DialogHeader>
          {selectedScan && (
            <div className="space-y-6 py-4">
              {/* Severity Summary */}
              <div className="grid grid-cols-5 gap-2">
                {[
                  { key: 'critical', label: 'Kritiek', color: 'bg-red-500' },
                  { key: 'high', label: 'Hoog', color: 'bg-orange-500' },
                  { key: 'medium', label: 'Medium', color: 'bg-yellow-500' },
                  { key: 'low', label: 'Laag', color: 'bg-blue-400' },
                  { key: 'info', label: 'Info', color: 'bg-neutral-400' },
                ].map(({ key, label, color }) => (
                  <div key={key} className="text-center">
                    <div className={`${color} text-white text-lg font-bold py-2 rounded-lg`}>{selectedScan.vulnerability_summary?.[key] || 0}</div>
                    <p className="text-xs text-neutral-500 mt-1">{label}</p>
                  </div>
                ))}
              </div>

              {/* Scanned Targets */}
              <div>
                <h4 className="text-sm font-medium text-black mb-2">Gescande Targets</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedScan.targets?.map((t, i) => <Badge key={i} variant="outline" className="border-neutral-200">{t.value}</Badge>)}
                </div>
              </div>

              {/* Vulnerabilities — Enhanced detail view */}
              {selectedScan.vulnerabilities?.length > 0 ? (
                <div>
                  <h4 className="text-sm font-medium text-black mb-3">Gevonden Kwetsbaarheden ({selectedScan.vulnerabilities.length})</h4>
                  <div className="space-y-3">
                    {selectedScan.vulnerabilities.map(vuln => {
                      const sevColors = {
                        critical: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', badge: 'bg-red-100 text-red-800', bar: 'bg-red-500' },
                        high: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-800', badge: 'bg-orange-100 text-orange-800', bar: 'bg-orange-500' },
                        medium: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-800', badge: 'bg-yellow-100 text-yellow-800', bar: 'bg-yellow-500' },
                        low: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800', badge: 'bg-blue-100 text-blue-800', bar: 'bg-blue-400' },
                        info: { bg: 'bg-neutral-50', border: 'border-neutral-200', text: 'text-neutral-700', badge: 'bg-neutral-100 text-neutral-700', bar: 'bg-neutral-400' },
                      };
                      const sc = sevColors[vuln.severity] || sevColors.info;
                      const recommendation = getVulnRecommendation(vuln.name);

                      return (
                        <div key={vuln.id} className={`rounded-lg border ${sc.border} ${sc.bg} p-4 space-y-3`} data-testid={`vuln-detail-${vuln.id}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge className={sc.badge}>{getSeverityLabel(vuln.severity)}</Badge>
                                <span className="text-[10px] text-neutral-400 font-mono">{vuln.id.slice(0, 8).toUpperCase()}</span>
                              </div>
                              <h5 className={`text-sm font-semibold ${sc.text}`}>{vuln.name}</h5>
                              <p className="text-xs text-neutral-600 mt-1">{vuln.description}</p>
                            </div>
                            <Badge variant="outline" className="border-neutral-200 text-[10px] shrink-0">{vuln.target_value}</Badge>
                          </div>

                          {/* Status & Detected */}
                          <div className="flex items-center gap-4 text-[10px] text-neutral-500">
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" strokeWidth={1.5} />
                              Gedetecteerd: {new Date(vuln.detected_at).toLocaleDateString('nl-NL')}
                            </span>
                            <span className="flex items-center gap-1">
                              {vuln.status === 'resolved' ? <CheckCircle className="h-3 w-3 text-green-500" strokeWidth={1.5} /> : <AlertTriangle className="h-3 w-3 text-amber-500" strokeWidth={1.5} />}
                              Status: {vuln.status === 'resolved' ? 'Opgelost' : vuln.status === 'in_progress' ? 'In behandeling' : 'Open'}
                            </span>
                          </div>

                          {/* Recommendation */}
                          {recommendation && (
                            <div className="p-2.5 rounded bg-white/70 border border-neutral-100">
                              <p className="text-[10px] font-bold text-neutral-500 uppercase tracking-wider mb-2">Aanbeveling</p>
                              <p className="text-xs text-neutral-700 leading-relaxed">{recommendation}</p>
                            </div>
                          )}

                          {/* Create measure from vulnerability */}
                          {vuln.status !== 'resolved' && (
                            <div className="flex justify-end">
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs border-neutral-200 hover:bg-neutral-100"
                                onClick={() => handleCreateMeasureFromVuln(vuln)}
                                disabled={creatingMeasure === vuln.id}
                                data-testid={`create-measure-${vuln.id}`}
                              >
                                {creatingMeasure === vuln.id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Wrench className="h-3 w-3 mr-1" strokeWidth={1.5} />}
                                Maatregel aanmaken
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="py-8 text-center bg-green-50 rounded-lg">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-2" strokeWidth={1.5} />
                  <p className="text-green-700 font-medium">Geen kwetsbaarheden gevonden</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setShowScanDetailsModal(false)}>Sluiten</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default TechnologyPage;
